'use strict';

/**
 * Import type definitions allowing VS Code to show IntelliSense.
 *
 * @typedef {import('./CommonMethods').default} CommonMethods
 */

class Embed {
    /**
     * Create a new instance.
     *
     * @param {CommonMethods} commonMethods
     */
    constructor (commonMethods) {
        this.logger = console
        this.commonMethods = commonMethods;

        this.commonMethods.isDomReady().then(() => {
            return this.commonMethods.waitForExplicitConsent({ timeoutInSeconds: 5 })
        }).catch(() => {
            this.logger.warn('Did not get an explicit consent.');
        }).finally(() => {
            window.document.documentElement.classList.add('embeds-consent-available');
            this.findAndActivateEmbeds()
        });

        /**
         * @see https://docs.usercentrics.com/#/v2-events?id=uc_ui_cmp_event
         */
        window.addEventListener('UC_UI_CMP_EVENT', () => {
            this.findAndActivateEmbeds()
        })
    }

    /**
     * Find all active embeds in the DOM and check their consent.
     */
    async findAndActivateEmbeds () {
        const embeds = document.querySelectorAll('.c-embed[data-uctemplateid][data-vendorname]:not(.is-loaded)');

        if (embeds.length === 0) {
            return;
        }

        this.restartConsentEmbeddings();

        for (const embedElement of embeds) {
            try {
                await this.checkConsentAndActivate(embedElement);
            } catch (error) {
                this.logger.log(error);
            }
        }
    }

    /**
     * If Usercentrics is available, programmatically rerender the embeddings content.
     * Does nothing if Usercentrics isn’t available or not yet initialized.
     */
    async restartConsentEmbeddings () {
        try {
            await this.commonMethods.waitForCmpIsAvailable();
            await window.UC_UI.restartEmbeddings();
        } catch (error) {
            this.logger.error('Failed to restart Usercentrics embeddings', error);
            // do nothing
        }
    }

    /**
     * Check the given `embedElement` on whether to show the consent
     * handler or activate the embed code to run the processing.
     */
    async checkConsentAndActivate (embedElement) {
        const vendorName = embedElement.dataset.vendorname;
        const vendorTemplateId = embedElement.dataset.uctemplateid;

        const optinButton = embedElement.querySelector('.c-embed__optinbutton:not(.has-clickhandler');

        if (optinButton) {
            optinButton.classList.add('has-clickhandler');
            optinButton.addEventListener('click', () => {
                this.logger.log('Accept button clicked', embedElement);
                optinButton.classList.add('disabled');

                this.activateEmbed(embedElement);

                /**
                 * We don’t need to accept the given consent manually here. The frontend markup
                 * uses the Usercentrics "Embeddings". The related Usercentrics `uc-<x>` tags
                 * tell their JavaScript to pick up the HTML and do the magic automatically.
                 *
                 * @see https://docs.usercentrics.com/#/v2-embeddings
                 */
            }, { once: true });
        }

        const services = await this.commonMethods.getConsents([vendorTemplateId]);

        if (services.length !== 1) {
            throw new Error('Unexpected number of services returned');
        }

        const service = services[0];

        if (service.consent.status === true) {
            return this.activateEmbed(embedElement)
        }

        this.logger.log(`No consent for ${vendorName}`, { service });

        /** @type {HTMLDivElement | null} */
        const previewArea = embedElement.querySelector('.c-embed__preview');

        if (previewArea) {
            previewArea.classList.remove('hide')
        }
    }

    /**
     * Run the processing of the related embed code.
     */
    activateEmbed (embedElement) {
        this.logger.log('Activating embed', embedElement);

        if (embedElement.classList.contains('is-loaded')) {
            this.logger.warn('Embed already loaded');
            return;
        }

        embedElement.classList.add('is-loaded');

        const xmp = embedElement.querySelector('xmp');
        const fillArea = embedElement.querySelector('.c-embed__live');
        const previewArea = embedElement.querySelector('.c-embed__preview');

        const range = document.createRange();
        range.setStart(embedElement, 0);
        fillArea.appendChild(range.createContextualFragment(xmp.textContent));
        previewArea.classList.add('hide');
        previewArea.setAttribute('aria-hidden', 'true');

        embedElement.classList.add('is-initialized');
    }
}

export default Embed;
